import { useCallback, useEffect, useMemo, useState } from 'react'
import { useUserRole } from '@/hooks/useUserRole'
import { deletePaymentLogOrAdjustment } from '@/lib/api/paymentService'
import { showApiError, showSuccess } from '@/lib/utils/notifications'
import {
  getGatewayStatuses,
  getOutstandingBalanceApplicationInfo,
  getOutstandingBalanceList,
  sendInvoiceReminder,
} from '../services/reportsService'
import type { BalanceReportListParams, BalanceReportSortKey } from '../types/pages'
import type {
  GatewayStatusMetaMap,
  OutstandingBalanceApplicationInfo,
  OutstandingBalanceListResponse,
  OutstandingBalancePaymentLogRow,
  OutstandingBalanceRow,
} from '../types/api'
import { DEFAULT_BALANCE_REPORT_LIST_PARAMS } from '../constants/balanceReportPage'
import {
  buildOutstandingExportHref,
  buildOutstandingQueryParams,
  canDeleteBalanceNestedPayment,
  canEditBalanceNestedPayment,
  countActiveFilters,
  findContractPayment,
  initialParams,
} from '../utils/balanceReportPageHelpers'
import {
  buildOutstandingBalanceSessionFromParams,
  clearOutstandingBalanceSession,
  writeOutstandingBalanceSession,
} from '../utils/outstandingBalanceSessionFilter'

export function useBalanceReportPage() {
  const { hasWriteAccess, hasPolicy, companyId, propertyId } = useUserRole()
  const canOpenApplication = hasWriteAccess('application')
  const canPerformPaymentActions = hasPolicy([
    'application_detail_payment_log_action',
  ])
  const lockedCompanyId = companyId != null ? String(companyId) : undefined
  const lockedPropertyId = propertyId != null ? String(propertyId) : undefined

  const [params, setParams] = useState<BalanceReportListParams>(initialParams)
  const [searchDraft, setSearchDraft] = useState(() => params.q)
  const [rows, setRows] = useState<OutstandingBalanceRow[]>([])
  const [totals, setTotals] =
    useState<OutstandingBalanceListResponse['total_count']>()
  const [pageOptions, setPageOptions] = useState({
    current: 1,
    last: 1,
    total: 0,
  })
  const [loading, setLoading] = useState(false)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
  const [adjustmentLoadingKey, setAdjustmentLoadingKey] = useState<
    string | null
  >(null)
  const [deletePaymentLoadingKey, setDeletePaymentLoadingKey] = useState<
    string | null
  >(null)
  const [deleteConfirm, setDeleteConfirm] = useState<{
    payment: OutstandingBalancePaymentLogRow
    key: string
  } | null>(null)
  const [adjustmentModal, setAdjustmentModal] = useState<{
    payment: OutstandingBalancePaymentLogRow | null
    application: OutstandingBalanceApplicationInfo | null
  }>({
    payment: null,
    application: null,
  })
  const [invoiceRow, setInvoiceRow] = useState<OutstandingBalanceRow | null>(
    null,
  )
  const [invoiceSending, setInvoiceSending] = useState(false)
  const [gatewayMeta, setGatewayMeta] = useState<GatewayStatusMetaMap>({})

  useEffect(() => {
    setSearchDraft(params.q)
  }, [params.q])

  useEffect(() => {
    void getGatewayStatuses()
      .then(setGatewayMeta)
      .catch(() => {
        /* rows fall back to the raw gateway status */
      })
  }, [])

  const load = useCallback(
    async (next: BalanceReportListParams) => {
      setLoading(true)
      try {
        const scoped = {
          ...next,
          company_id: lockedCompanyId || next.company_id,
          property_id: lockedPropertyId || next.property_id,
        }
        const res = await getOutstandingBalanceList(
          buildOutstandingQueryParams(scoped),
        )
        setRows(res.data ?? [])
        setTotals(res.total_count)
        setPageOptions({
          current: res.current ?? 1,
          last: Math.max(1, res.last ?? 1),
          total: res.total ?? 0,
        })
        writeOutstandingBalanceSession(
          buildOutstandingBalanceSessionFromParams(scoped),
        )
      } catch (err) {
        showApiError(err)
      } finally {
        setLoading(false)
      }
    },
    [lockedCompanyId, lockedPropertyId],
  )

  useEffect(() => {
    void load(params)
  }, [load, params])

  useEffect(() => {
    const timer = setTimeout(() => {
      const q = searchDraft.trim()
      if (q !== params.q.trim()) setParams((prev) => ({ ...prev, q, page: 1 }))
    }, 500)
    return () => clearTimeout(timer)
  }, [searchDraft, params.q])

  const sort = (orderBy: BalanceReportSortKey) => {
    setParams((prev) => ({
      ...prev,
      orderBy,
      sortBy:
        prev.orderBy === orderBy && prev.sortBy === 'desc' ? 'asc' : 'desc',
      page: 1,
    }))
  }

  const resetFilters = () => {
    setSearchDraft('')
    clearOutstandingBalanceSession()
    setExpandedRows(new Set())
    setParams({ ...DEFAULT_BALANCE_REPORT_LIST_PARAMS })
  }

  const exportHref = useMemo(
    () =>
      buildOutstandingExportHref({
        ...params,
        company_id: lockedCompanyId || params.company_id,
        property_id: lockedPropertyId || params.property_id,
      }),
    [params, lockedCompanyId, lockedPropertyId],
  )

  const toggleExpanded = (key: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const openPaymentAdjustment = async (
    row: OutstandingBalanceRow,
    key: string,
    paymentDetail?: OutstandingBalancePaymentLogRow | null,
  ) => {
    if (row.applicant_id == null) return
    setAdjustmentLoadingKey(key)
    try {
      const application = await getOutstandingBalanceApplicationInfo(
        row.applicant_id,
      )
      const payment = paymentDetail ?? findContractPayment(application)
      if (!payment) {
        showApiError({
          message: 'No adjustable contract payment was found for this applicant.',
        })
        return
      }
      setAdjustmentModal({ payment, application })
    } catch (err) {
      showApiError(err)
    } finally {
      setAdjustmentLoadingKey(null)
    }
  }

  const closeAdjustmentModal = () =>
    setAdjustmentModal({ payment: null, application: null })

  const canEditPayment = useCallback(
    (payment: OutstandingBalancePaymentLogRow) =>
      canEditBalanceNestedPayment(payment, hasPolicy),
    [hasPolicy],
  )

  const canDeletePayment = useCallback(
    (payment: OutstandingBalancePaymentLogRow) =>
      canDeleteBalanceNestedPayment(payment, hasPolicy),
    [hasPolicy],
  )

  const requestDeletePayment = (
    payment: OutstandingBalancePaymentLogRow,
    key: string,
  ) => {
    setDeleteConfirm({ payment, key })
  }

  const cancelDeletePayment = () => setDeleteConfirm(null)

  const confirmDeletePayment = async () => {
    if (!deleteConfirm) return
    const { payment, key } = deleteConfirm
    setDeleteConfirm(null)
    setDeletePaymentLoadingKey(key)
    try {
      await deletePaymentLogOrAdjustment({
        payment_log_id: payment.payment_log_id,
        pa_id: payment.pa_id ?? payment.payment_adjustment_id,
      })
      showSuccess('Payment Adjustment Deleted Successfully')
      await load(params)
    } catch (err) {
      showApiError(err)
    } finally {
      setDeletePaymentLoadingKey(null)
    }
  }

  const submitInvoiceReminder = async (data: {
    email: boolean
    message: boolean
  }) => {
    if (!invoiceRow?.applicant_id) return
    setInvoiceSending(true)
    try {
      await sendInvoiceReminder(invoiceRow.applicant_id, {
        email: data.email,
        message: data.message,
      })
      showSuccess('Invoice Send Successfully!')
      setInvoiceRow(null)
    } catch (err) {
      showApiError(err)
    } finally {
      setInvoiceSending(false)
    }
  }

  return {
    canOpenApplication,
    canPerformPaymentActions,
    gatewayMeta,
    lockedCompanyId,
    lockedPropertyId,
    params,
    setParams,
    searchDraft,
    setSearchDraft,
    rows,
    totals,
    pageOptions,
    loading,
    filtersOpen,
    setFiltersOpen,
    expandedRows,
    adjustmentLoadingKey,
    deletePaymentLoadingKey,
    deleteConfirm,
    adjustmentModal,
    invoiceRow,
    setInvoiceRow,
    invoiceSending,
    load,
    sort,
    resetFilters,
    exportHref,
    toggleExpanded,
    openPaymentAdjustment,
    closeAdjustmentModal,
    canEditPayment,
    canDeletePayment,
    requestDeletePayment,
    cancelDeletePayment,
    confirmDeletePayment,
    submitInvoiceReminder,
    activeFilterCount: countActiveFilters(
      params,
      lockedCompanyId,
      lockedPropertyId,
    ),
  }
}
